import { DISCLOSURE_INVITE_PARAM_NAME } from './constants'
import { Institution } from './enums'
import { type Language, languageEnumToBasename } from './localization'

export const buildUrl = (url: string, params: Record<string, string | string[]>) => {
  const result = new URL(url)
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => result.searchParams.append(`${key}`, item))
    } else {
      result.searchParams.append(key, value)
    }
  })
  return result.toString()
}

export const getRootDomain = (url: string) => {
  try {
    const { hostname } = new URL(url)
    return hostname.split('.').slice(-2).join('.')
  } catch {
    return 'faceup.com'
  }
}

export const generateShortIdUrl = (
  url: string,
  shortId: string | null,
  institution: Institution | null,
  language: string | null = null,
  isCheck = false
) => {
  const shortIdPath = `${language ? `/${languageEnumToBasename(language)}` : ''}/${
    institution === Institution.School ? 's' : 'c'
  }/${shortId ?? ''}${isCheck ? '/check' : ''}`

  return `${url}${shortIdPath}`
}

export const generateSurveyUrl = (url: string, shortId: string | null, language?: Language) => {
  const surveyPath = `${language ? `/${languageEnumToBasename(language)}` : ''}/q/${shortId ?? ''}`

  return `${url}${surveyPath}`
}

/** The public form's path, without the leading slash — e.g. `en/d/abc123`. */
export const generateDisclosurePath = (shortId: string | null, language?: Language) =>
  `${language ? `${languageEnumToBasename(language)}/` : ''}d/${shortId ?? ''}`

export const generateDisclosureUrl = (url: string, shortId: string | null, language?: Language) =>
  `${url}/${generateDisclosurePath(shortId, language)}`

/**
 * The path an invitation or reminder links to — e.g. `en/d/abc123?token=<token>`.
 */
export const generateDisclosureInvitePath = (
  shortId: string | null,
  inviteToken: string,
  language?: Language
) =>
  buildPath(generateDisclosurePath(shortId, language), {
    [DISCLOSURE_INVITE_PARAM_NAME]: inviteToken,
  })

const stubBase = 'https://example.com/'
/**
 * When you just want to create the path, not the whole URL.
 * I'm using the stubBase, so I can use the buildUrl which uses URL which needs the domain part.
 */
export const buildPath = (base: string, params: Record<string, string | string[]>) =>
  buildUrl(`${stubBase}${base}`, params).replace(stubBase, '')

// `location.pathname` never carries a query or hash, so any URL compared against it has to
// drop both first. Menu links that keep filters in the query, or anchor into a tab
// (`/report/:id/overview#comments`), otherwise never match the page they point at.
export const getPathnameOnly = (url: string) => url.split('?')[0]?.split('#')[0] ?? url

// The URL parser strips every ASCII tab and newline before resolving, and treats a backslash as
// a slash in special schemes, so `/\n/evil.com` and `/\evil.com` are authorities in disguise.
// Normalize first, then reject anything that opens one - this holds whatever base we resolve
// against, which is what keeps the check independent of the sentinel below.
const hasAuthority = (value: string) => /^[/\\]{2}/.test(value.replace(/[\t\n\r]/g, ''))

// Deliberately not `stubBase`: a reserved TLD (RFC 2606) can never be registered, so no input can
// name this host and make an explicit authority look like an origin inherited from the base.
const redirectStubBase = 'https://stub.invalid/'

/**
 * A redirect target taken from user input (e.g. a `?fallback=` query param) is only safe if it
 * is a same-origin absolute path. Testing the first characters is not enough, hence the
 * normalization in `hasAuthority`; resolving alone is not enough either, because a value naming
 * the base's own host is indistinguishable from one inheriting its origin. The base stands in for
 * the real one so this stays usable outside the browser (the file is imported by the backend too).
 */
export const isSafeRedirectPath = (value: string | null | undefined): value is string => {
  if (typeof value !== 'string' || !value.startsWith('/') || hasAuthority(value)) {
    return false
  }
  try {
    return new URL(value, redirectStubBase).origin === new URL(redirectStubBase).origin
  } catch {
    return false
  }
}
